// ============================================================
// src/components/Login.jsx
// Pantalla de autenticación: registro e inicio de sesión
// ============================================================

import React, { useState } from 'react';
import { auth } from '../services/firebase';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  updateProfile,
} from 'firebase/auth';

export default function Login() {
  // Modo: 'login' o 'register'
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Mensajes de error amigables en español
  const friendlyError = (code) => {
    const messages = {
      'auth/user-not-found': 'No existe una cuenta con ese correo.',
      'auth/wrong-password': 'Contraseña incorrecta.',
      'auth/email-already-in-use': 'Ese correo ya está registrado.',
      'auth/weak-password': 'La contraseña debe tener al menos 6 caracteres.',
      'auth/invalid-email': 'El formato del correo no es válido.',
      'auth/too-many-requests': 'Demasiados intentos. Intenta más tarde.',
      'auth/invalid-credential': 'Correo o contraseña incorrectos.',
    };
    return messages[code] || 'Ocurrió un error. Intenta de nuevo.';
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (mode === 'register') {
        // Crear nueva cuenta
        const { user } = await createUserWithEmailAndPassword(auth, email, password);
        // Guardar nombre de display
        await updateProfile(user, { displayName: name });
      } else {
        // Iniciar sesión
        await signInWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo y título */}
        <div className="text-center mb-8">
          <div className="text-7xl mb-3"><img src="logo.png" alt="" className="w-20 h-20 mx-auto object-contain"/></div>
          <h1 className="text-4xl font-extrabold text-white">OpenMind</h1>
          <p className="text-indigo-200 mt-2">Aprende a pensar, no a memorizar</p>
        </div>

        {/* Card del formulario */}
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          {/* Pestañas login / registro */}
          <div className="flex bg-gray-100 rounded-2xl p-1 mb-6">
            <button
              onClick={() => { setMode('login'); setError(''); }}
              className={`flex-1 py-2 rounded-xl text-sm font-semibold transition
                ${mode === 'login' ? 'bg-white shadow text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Iniciar sesión
            </button>
            <button
              onClick={() => { setMode('register'); setError(''); }}
              className={`flex-1 py-2 rounded-xl text-sm font-semibold transition
                ${mode === 'register' ? 'bg-white shadow text-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              Crear cuenta
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Campo nombre (solo en registro) */}
            {mode === 'register' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre completo
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Tu nombre"
                  required
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 transition"
                />
              </div>
            )}

            {/* Campo correo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Correo electrónico
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="correo@ejemplo.com"
                required
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 transition"
              />
            </div>

            {/* Campo contraseña */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Contraseña
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'register' ? 'Mínimo 6 caracteres' : '••••••••'}
                required
                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 transition"
              />
            </div>

            {/* Mensaje de error */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                ⚠️ {error}
              </div>
            )}

            {/* Botón principal */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold py-3 rounded-xl hover:opacity-90 transition disabled:opacity-60 disabled:cursor-not-allowed text-sm"
            >
              {loading
                ? '...'
                : mode === 'register'
                ? '🚀 Crear mi cuenta gratis'
                : '→ Entrar a OpenMind'}
            </button>
          </form>

          {/* Propuesta de valor para registro */}
          {mode === 'register' && (
            <div className="mt-5 grid grid-cols-3 gap-2 text-center">
              {[
                { icon: '🎯', label: '120 temas' },
                { icon: '🏆', label: 'Gamificado' },
                { icon: '🧠', label: 'Pensamiento real' },
              ].map(({ icon, label }) => (
                <div key={label} className="bg-indigo-50 rounded-xl py-2">
                  <div className="text-xl">{icon}</div>
                  <div className="text-xs text-indigo-700 font-medium">{label}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-indigo-200 text-xs mt-6">
          6 materias · 4 niveles · 0 memorización
        </p>
      </div>
    </div>
  );
}
